import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import { change, getFormValues, isValid } from 'redux-form';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { FormName } from '@topconagriculture/feed-types';

import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import {
  getNextOrderData,
  fetchOrderList,
  downloadOrder,
  emailOrder,
  getIngredientReceiveData,
  receiveIngredient,
  updateRecord,
  deleteRecord,
  updateDeliveryIngredient,
} from './orders.actions';
import { composeErrorMessage, transformFormValueToNumber } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getSelectedOrderId } from '../../selected/selected.selector';
import { setSelectedOrderId } from '../../selected/selected.actions';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { updateTransactionsReportRow } from '../reports/reports.actions';
import { toLocalDate, toTimezoneDate } from '../../../core/helpers/utils/dateUtils';
import { settingsSelector } from '../../selectors';
import { Forms } from '../../form/form.selector';
import { getInventoryTransactionsReportSummaryItemWeight } from '../inventory/inventory.selector';

function* getOrderListSaga() {
  yield put(showLoader(LoadersNames.fetchOrders));
  try {
    const result = yield call(api.orders.getOrderList);
    const selectedOrderId = yield select(getSelectedOrderId);
    const isResultIncludeSelectedResult = !!result.find(({ id }) => id === selectedOrderId);

    if (!isResultIncludeSelectedResult) {
      yield put(setSelectedOrderId(result[0]?.id || null));
    }

    yield put(fetchOrderList.done({ params: {}, result }));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchOrders));
}

function* getNextOrderDataSaga() {
  try {
    yield call(api.orders.getNextOrderNumber, { typeCode: 1 });
  } catch (err) {
    // api always returns error in this case, but response includes order number
    if (err.status === 409) {
      yield put(getNextOrderData.done({ params: {}, result: err.data }));
    } else {
      yield put(notificationError(composeErrorMessage(err)));
    }
  }
}

function* downloadOrderSaga({ payload }) {
  try {
    yield call(api.orders.downloadOrder, payload.orderId, payload.fileName);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

function* emailOrderSaga({ payload }) {
  try {
    const result: string = yield call(api.orders.emailOrder, payload.orderId);
    const words = result.split(' ');
    const email = words.pop();

    yield put(notificationSuccess('inventory:mail_queued', { email }));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

function* updateRecordSaga(action: ReturnType<typeof updateRecord.started>) {
  const { payload, meta } = action;
  const { record, ignoreStock, id, stockTransactionId } = payload;
  yield put(showLoader(LoadersNames.updateRecord));
  try {
    const result = yield call(
      api.orders.updateRecord,
      id,
      stockTransactionId,
      record,
      ignoreStock,
      FormName.directDeliveryItemEdit,
    );
    yield put(
      updateRecord.done({
        result,
        params: payload,
      }),
    );
    successHandler(meta);
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateRecord));
}

function* deleteRecordSaga(action: ReturnType<typeof deleteRecord.started>) {
  const { payload, meta } = action;
  const { stockTransactionId, stockTransactionitemId, ignoreStock } = payload;
  yield put(showLoader(LoadersNames.deleteRecord));
  try {
    const result = yield call(api.orders.deleteRecord, stockTransactionId, stockTransactionitemId, ignoreStock);
    yield put(
      deleteRecord.done({
        result,
        params: payload,
      }),
    );
    successHandler(meta);
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.deleteRecord));
}

function* getReceiveDataSaga(action: ReturnType<typeof getIngredientReceiveData.started>) {
  try {
    const { stockTransactionId, stockTransactionItemId, formName, loader, multiplier, validateForm } = action.payload;
    const {
      actualDate,
      actualDryMatter,
      actualPrice,
      actualPriceDate,
      actualWeight,
      invoiceNo,
      referenceNo,
      partialReceive,
      notes,
      weighbridgeTicketIds,
    } = yield select(getFormValues(Forms.stocktransactionItemReceive));
    const formIsValid = yield select(isValid(Forms.stocktransactionItemReceive));
    const timezone = yield select(settingsSelector.getTimeZone);
    const data = {
      actualDate: actualDate ? toTimezoneDate(toLocalDate(actualDate), timezone) : undefined,
      actualPriceDate: actualPriceDate ? toTimezoneDate(toLocalDate(actualPriceDate), timezone) : undefined,
      actualDryMatter: actualDryMatter ? transformFormValueToNumber(actualDryMatter) : undefined,
      actualWeight: actualWeight ? transformFormValueToNumber(actualWeight) * multiplier : undefined,
      actualPrice: actualPrice ? transformFormValueToNumber(actualPrice) / multiplier : undefined,
      notes,
      weighbridgeTicketIds,
    };

    if (loader) {
      yield put(showLoader(LoadersNames.fetchReceiveIngredient));
    }

    function* sendRequest() {
      const result = yield call(
        api.orders.receiveIngredient,
        stockTransactionId,
        stockTransactionItemId,
        data,
        formName,
        true,
      );
      yield put(getIngredientReceiveData.done({ params: action.payload, result }));
      yield put(change(Forms.stocktransactionItemReceive, 'invoiceNo', invoiceNo));
      yield put(change(Forms.stocktransactionItemReceive, 'referenceNo', referenceNo));
      yield put(change(Forms.stocktransactionItemReceive, 'partialReceive', partialReceive));
    }

    if (validateForm) {
      if (formIsValid) {
        yield call(sendRequest);
      }
    } else {
      yield call(sendRequest);
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  } finally {
    yield put(hideLoader(LoadersNames.fetchReceiveIngredient));
  }
}

function* updateOrder(payload) {
  const { stockTransactionId, stockTransactionItemId, multiplier, formName } = payload;
  const { referenceNo, invoiceNo, weighbridgeTicketIds, ...data } = yield select(
    getFormValues(Forms.stocktransactionItemReceive),
  );
  const timezone = yield select(settingsSelector.getTimeZone);

  const receivedIngredient = yield call(
    api.orders.receiveIngredient,
    stockTransactionId,
    stockTransactionItemId,
    {
      ...data,
      weighbridgeTicketIds,
      actualDate: toTimezoneDate(toLocalDate(data.actualDate), timezone),
      actualPriceDate: toTimezoneDate(toLocalDate(data.actualPriceDate), timezone),
      actualPrice: transformFormValueToNumber(data.actualPrice) / multiplier,
      actualWeight: transformFormValueToNumber(data.actualWeight) * multiplier,
      actualDryMatter: transformFormValueToNumber(data.actualDryMatter),
      invoiceNo,
    },
    formName,
  );

  const updatedOrder = yield call(api.orders.updateOrder, { referenceNo }, formName, stockTransactionId);
  yield call(getOrderListSaga);
  return { receivedIngredient, updatedOrder };
}

function* receiveIngredientSaga(action: ReturnType<typeof receiveIngredient.started>) {
  try {
    yield put(showLoader(LoadersNames.fetchReceiveIngredient));

    yield call(updateOrder, action.payload);

    if (action.meta && action.meta.onSuccess) {
      action.meta.onSuccess();
    }

    yield put(notificationSuccess('inventory:ingredient_received'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  } finally {
    yield put(hideLoader(LoadersNames.fetchReceiveIngredient));
  }
}

function* updateDeliveryIngredientSaga(action: ReturnType<typeof updateDeliveryIngredient.started>) {
  try {
    const { multiplier } = action.payload;
    const priceUnit = yield select(getInventoryTransactionsReportSummaryItemWeight);
    yield put(showLoader(LoadersNames.fetchReceiveIngredient));

    const { receivedIngredient, updatedOrder } = yield call(updateOrder, action.payload);

    const order = {
      ...receivedIngredient,
      referenceNo: updatedOrder.referenceNo,
      invoiceNo: receivedIngredient.invoiceNo,
      actualPrice: receivedIngredient.actualPrice * multiplier,
      actualWeight: receivedIngredient.actualWeight / multiplier,
      plannedWeight: receivedIngredient.plannedWeight / multiplier,
      actualDate: receivedIngredient.receiveData.inventoryDate,
      actualPriceDate: receivedIngredient.receiveData.priceDate,
      summaryWeightMultiplier: multiplier / priceUnit.value1,
    };
    yield put(updateTransactionsReportRow.done({ result: order, params: multiplier / priceUnit.value1 }));

    if (action.meta && action.meta.onSuccess) {
      action.meta.onSuccess();
    }

    yield put(notificationSuccess('inventory:ingredient_received'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  } finally {
    yield put(hideLoader(LoadersNames.fetchReceiveIngredient));
  }
}

export function* watchOrdersSaga() {
  yield all([
    yield takeLatest(fetchOrderList.started, getOrderListSaga),
    yield takeLatest(getNextOrderData.started, getNextOrderDataSaga),
    yield takeLatest(downloadOrder.started, downloadOrderSaga),
    yield takeLatest(emailOrder.started, emailOrderSaga),
    yield takeLatest(updateRecord.started, updateRecordSaga),
    yield takeLatest(deleteRecord.started, deleteRecordSaga),
    yield takeLatest(getIngredientReceiveData.started, getReceiveDataSaga),
    yield takeLatest(receiveIngredient.started, receiveIngredientSaga),
    yield takeLatest(updateDeliveryIngredient.started, updateDeliveryIngredientSaga),
  ]);
}
